<template>
  <BaseModal
    id="shipping-calculator-modal"
    :value="value"
    @input="
      e => {
        if (!e) {
          $emit('close');
          $emit('input', false);
        }
      }
    "
  >
    <template v-slot:header>
      {{ $t("shipping_calculator.title") }}
    </template>
    <div class="body flex flex-col p-2">
      <div class="other-info-form flex flex-col flex-shrink-0">
        <button
          @click="showInfoForm = !showInfoForm"
          class="flex items-center justify-between bg-gray-500 p-3 rounded-t-md text-white shadow-lg hover:bg-gray-600 transition-colors"
        >
          <span class="font-semibold">{{
            $t("shipping_calculator.shipping_information")
          }}</span>
          <span class="text-xl">{{ showInfoForm ? "▼" : "▶" }}</span>
        </button>
        <div
          v-if="showInfoForm"
          class="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-400 p-2 items-center text-white justify-between shadow-lg"
        >
          <div class="flex flex-col">
            <label for="country">{{ $t("shipping_calculator.country") }}</label>
            <input
              type="text"
              class="regular bordered full"
              :value="intCountry"
              disabled
            />
          </div>
          <div class="flex flex-col">
            <label for="state">{{ $t("shipping_calculator.state") }}</label>
            <select
              class="regular full bordered"
              type="text"
              name="state"
              v-model="intState"
            >
              <option
                v-for="state in statesFixed"
                :value="state.shortCode"
                :key="state.shortCode"
                >{{ state.label }}</option
              >
            </select>
          </div>
          <div class="flex flex-col">
            <label for="postal-code">{{
              $t("shipping_calculator.postal_code")
            }}</label>
            <input
              type="text"
              id="postal-code"
              v-model="intPostalCode"
              class="bordered regular ml-1"
            />
          </div>
        </div>
        <div
          v-if="showInfoForm"
          class="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-400 p-2 rounded-b-md mt-1 items-center text-white justify-between shadow-lg"
        >
          <div>
            <label class="mr-4">{{
              $t("shipping_calculator.products_on_order")
            }}</label>
            <input
              type="text"
              class="regular bordered full"
              :value="productsInfo"
              disabled
            />
          </div>
          <div class="flex flex-col">
            <label for="residential">
              {{ $t("shipping_calculator.use_custom_package") }}
            </label>
            <select
              name="use-custom-package"
              id="use-custom-package"
              class="default-select bordered ml-1 full"
              v-model="useCustomPackage"
            >
              <option :value="true">{{ $t("shipping_calculator.yes") }}</option>
              <option :value="false">{{ $t("shipping_calculator.no") }}</option>
            </select>
          </div>
          <div class="flex flex-col">
            <label>{{ $t("shipping_calculator.address_type") }}</label>
            <ResidentialBadge
              class="ml-1"
              :residential="residentialCheck && residentialCheck.residential"
              :verified="residentialCheck && residentialCheck.verified"
              :status="residentialCheck && residentialCheck.status"
            />
          </div>
        </div>
      </div>
      <div class="flex flex-row gap-2 justify-center items-center mt-2">
        <button
          class="flex flex-row btn-info-button justify-center items-center w-full md:w-40"
          @click="calculate()"
        >
          <template v-if="!loading">
            {{ $t("shipping_calculator.calculate") }}
            <img class="w-4 ml-2" src="@/assets/icons/calculator.svg" />
          </template>
          <template v-else>
            <BaseMiniSpinner />
          </template>
        </button>
        <button
          class="flex flex-row btn-info-button justify-center items-center w-full md:w-auto px-3 bg-yellow-500 hover:bg-yellow-600"
          :disabled="loading"
          @click="calculate(true)"
          :title="$t('shipping_calculator.force_recalculate')"
        >
          {{ $t("shipping_calculator.force_recalculate") }}
        </button>
      </div>
      <div
        v-if="useCustomPackage"
        class="grid grid-cols-4 gap-4 bg-red-400 p-2 rounded-md mt-1 items-center text-white justify-between shadow-lg"
      >
        <div>
          <label for="height">{{ $t("shipping_calculator.height") }}</label>
          <input
            id="height"
            type="number"
            step="0.01"
            class="bordered regular full"
            v-model="customBox.height"
          />
        </div>
        <div>
          <label for="width">{{ $t("shipping_calculator.width") }}</label>
          <input
            id="width"
            type="number"
            step="0.01"
            class="bordered regular full"
            v-model="customBox.width"
          />
        </div>
        <div>
          <label for="length">{{ $t("shipping_calculator.length") }}</label>
          <input
            id="length"
            type="number"
            step="0.01"
            class="bordered regular full"
            v-model="customBox.length"
          />
        </div>
        <div>
          <label for="weight">{{ $t("shipping_calculator.weight") }}</label>
          <input
            id="weight"
            type="number"
            step="0.01"
            class="bordered regular full"
            v-model="customBox.weight"
          />
        </div>
      </div>

      <!-- Box Constraints Section -->
      <div class="mt-1">
        <div
          class="flex flex-row gap-2 bg-purple-500 p-2 rounded-t-md text-white shadow-lg items-start"
        >
          <div class="flex items-center">
            <input
              type="checkbox"
              id="use-box-constraints"
              v-model="useBoxConstraints"
              class="mr-2"
            />
            <label
              for="use-box-constraints"
              class="font-semibold cursor-pointer whitespace-nowrap"
            >
              {{ $t("shipping_calculator.override_box_constraints") }}
            </label>
          </div>
          <div
            v-if="useBoxConstraints"
            class="grid grid-cols-2 md:grid-cols-5 gap-2"
          >
            <div>
              <label for="max-length">{{
                $t("shipping_calculator.max_length")
              }}</label>
              <input
                id="max-length"
                type="number"
                step="0.01"
                :placeholder="$t('shipping_calculator.ups_default')"
                class="bordered regular full"
                v-model.number="boxConstraints.maxLength"
              />
            </div>
            <div>
              <label for="max-width">{{
                $t("shipping_calculator.max_width")
              }}</label>
              <input
                id="max-width"
                type="number"
                step="0.01"
                :placeholder="$t('shipping_calculator.ups_default')"
                class="bordered regular full"
                v-model.number="boxConstraints.maxWidth"
              />
            </div>
            <div>
              <label for="max-height">{{
                $t("shipping_calculator.max_height")
              }}</label>
              <input
                id="max-height"
                type="number"
                step="0.01"
                :placeholder="$t('shipping_calculator.ups_default')"
                class="bordered regular full"
                v-model.number="boxConstraints.maxHeight"
              />
            </div>
            <div>
              <label for="max-weight">{{
                $t("shipping_calculator.max_weight")
              }}</label>
              <input
                id="max-weight"
                type="number"
                step="0.01"
                :placeholder="$t('shipping_calculator.ups_default')"
                class="bordered regular full"
                v-model.number="boxConstraints.maxWeight"
              />
            </div>
            <div>
              <label for="max-length-girth">{{
                $t("shipping_calculator.max_length_girth")
              }}</label>
              <input
                id="max-length-girth"
                type="number"
                step="0.01"
                :placeholder="$t('shipping_calculator.ups_default')"
                class="bordered regular full"
                v-model.number="boxConstraints.maxLengthPlusGirth"
              />
            </div>
          </div>
        </div>
      </div>

      <template v-if="!loading">
        <div
          v-if="residentialCheck && residentialCheck.residential"
          class="residential-note rounded-md my-2 p-3"
        >
          <i class="mdi mdi-home"></i>
          {{ residentialNote }}
        </div>
        <div
          class="bg-red-400 text-black rounded-md my-2 p-4"
          v-for="(warning, index) in warnings"
          :key="index"
        >
          {{ warning }}
        </div>

        <!-- Empty order message -->
        <div
          v-if="!packageData && !options.length && warnings.length === 0"
          class="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-md my-2"
        >
          <p class="font-bold">
            {{ $t("shipping_calculator.no_shipping_data") }}
          </p>
          <p class="text-sm">
            {{ $t("shipping_calculator.empty_order_message") }}
          </p>
        </div>

        <!-- Boxes Summary -->
        <div
          class="flex flex-col mt-4"
          v-if="packageData && packageData.summary"
        >
          <div class="bg-blue-100 p-3 rounded-md shadow-md">
            <h2 class="font-bold text-lg mb-2">
              {{ $t("shipping_calculator.package_summary") }}
            </h2>
            <div class="grid grid-cols-3 gap-2">
              <div>
                <strong>{{ $t("shipping_calculator.total_boxes") }}</strong>
                {{ packageData.summary.totalBoxes }}
              </div>
              <div>
                <strong>{{ $t("shipping_calculator.total_weight") }}</strong>
                {{ packageData.summary.totalWeight }} Lbs
              </div>
              <div>
                <strong>{{ $t("shipping_calculator.total_items") }}</strong>
                {{ packageData.summary.totalItems }}
              </div>
            </div>
          </div>
        </div>

        <!-- Individual Boxes -->
        <div
          class="flex flex-col mt-4 gap-3"
          v-if="packageData && packageData.boxes"
        >
          <div
            v-for="(box, index) in packageData.boxes"
            :key="index"
            class="bg-white border-2 border-gray-300 rounded-md shadow-md"
          >
            <div
              class="bg-gray-200 p-3 rounded-t-md flex justify-between items-center cursor-pointer"
              @click="toggleBox(index)"
            >
              <div class="flex flex-col">
                <h3 class="font-bold text-md">
                  {{ $t("shipping_calculator.box_number") }}
                  {{ box.boxNumber }} ({{ box.itemCount }}
                  {{ $t("shipping_calculator.items") }})
                </h3>
                <div
                  class="text-xs text-gray-600 mt-1"
                  v-if="box.shipping_source_warehouse_name"
                >
                  <span class="font-semibold">{{
                    $t("shipping_calculator.source")
                  }}</span>
                  {{ box.shipping_source_warehouse_name }} ({{
                    box.shipping_source_warehouse_code
                  }})
                  <span class="text-gray-500">• {{ box.shipping_source }}</span>
                </div>
              </div>
              <button class="text-blue-600 font-semibold">
                {{
                  expandedBoxes[index]
                    ? $t("shipping_calculator.hide_details")
                    : $t("shipping_calculator.show_details")
                }}
              </button>
            </div>

            <div v-if="expandedBoxes[index]" class="p-3">
              <!-- Box Dimensions and Weight -->
              <div class="grid grid-cols-4 gap-2 mb-3 bg-blue-50 p-2 rounded">
                <div>
                  <strong>{{ $t("shipping_calculator.length") }}:</strong>
                  {{ box.usedDimensions.length }}"
                </div>
                <div>
                  <strong>{{ $t("shipping_calculator.width") }}:</strong>
                  {{ box.usedDimensions.width }}"
                </div>
                <div>
                  <strong>{{ $t("shipping_calculator.height") }}:</strong>
                  {{ box.usedDimensions.height }}"
                </div>
                <div>
                  <strong>{{ $t("shipping_calculator.weight") }}:</strong>
                  {{ box.totalWeight }} Lbs
                </div>
              </div>

              <!-- Items in Box -->
              <div class="mt-2">
                <h4 class="font-semibold mb-2">
                  {{ $t("shipping_calculator.items_in_box") }}
                </h4>
                <div class="overflow-x-auto">
                  <table class="w-full text-sm border-collapse">
                    <thead>
                      <tr class="bg-gray-100">
                        <th class="border p-2 text-left">
                          {{ $t("shipping_calculator.sku") }}
                        </th>
                        <th class="border p-2 text-left">
                          {{ $t("shipping_calculator.description") }}
                        </th>
                        <th class="border p-2 text-center">
                          {{ $t("shipping_calculator.qty") }}
                        </th>
                        <th class="border p-2 text-center">
                          {{ $t("shipping_calculator.unit_dims") }}
                        </th>
                        <th class="border p-2 text-center">
                          {{ $t("shipping_calculator.weight") }}
                        </th>
                        <th class="border p-2 text-center">Total Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, itemIndex) in box.items"
                        :key="itemIndex"
                      >
                        <td class="border p-2">{{ item.sku }}</td>
                        <td class="border p-2">{{ item.description }}</td>
                        <td class="border p-2 text-center">
                          {{ item.quantity }}
                        </td>
                        <td class="border p-2 text-center">
                          {{ item.length }}" × {{ item.width }}" ×
                          {{ item.height }}"
                        </td>
                        <td class="border p-2 text-center">
                          {{ item.weight }} Lbs
                        </td>
                        <td class="border p-2 text-center">
                          {{ item.totalWeight }} Lbs
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Shipping Options -->
        <div
          class="w-full mt-6 flex flex-col justify-center"
          v-if="options.length > 0"
        >
          <h2 class="font-bold text-lg mb-3">
            {{ $t("shipping_calculator.shipping_options") }}
          </h2>

          <!-- Carrier Filter Tabs -->
          <div class="flex flex-wrap gap-2 mb-4 border-b border-gray-300 pb-3">
            <button
              @click="selectedCarrier = 'all'"
              :class="[
                'flex items-center gap-2 px-4 py-2 rounded-t-lg font-semibold transition-all',
                selectedCarrier === 'all'
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              ]"
            >
              {{ $t("shipping_calculator.all_carriers") }} ({{
                options.length
              }})
            </button>
            <button
              v-for="carrier in availableCarriers"
              :key="carrier.name"
              @click="selectedCarrier = carrier.name"
              :class="[
                'flex items-center gap-2 px-4 py-2 rounded-t-lg font-semibold transition-all',
                selectedCarrier === carrier.name
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              ]"
            >
              <img
                v-if="carrier.logo"
                :src="carrier.logo"
                :alt="carrier.name"
                class="h-5"
              />
              <span>{{ carrier.name }} ({{ carrier.count }})</span>
            </button>
          </div>

          <!-- Shipping Options List -->
          <div
            v-for="(option, index) in filteredOptions"
            :key="index"
            @click="
              option.eligible === false ? null : selectShippingOption(option)
            "
            :class="[
              'border border-light-600 rounded-md flex flex-row px-4 py-6 my-1 shadow-md',
              option.eligible === false
                ? 'opacity-60 bg-gray-50 cursor-not-allowed'
                : 'cursor-pointer hover:border-blue-500 hover:ring-2 hover:drop-shadow-lg hover:border-2'
            ]"
          >
            <div class="flex-grow">
              <div class="font-semibold">
                {{ option.company }} - {{ option.service }}
              </div>
              <div
                v-if="option.localDeliveryType && option.eligible === false"
                class="inline-block mt-1 bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-semibold"
              >
                {{ localDeliveryReason(option.localDeliveryType) }}
              </div>
              <div
                v-else-if="option.localDeliveryType"
                class="inline-block mt-1 bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-semibold"
              >
                {{ localDeliveryLabel(option.localDeliveryType) }}
              </div>
              <div
                v-if="
                  option.eligible !== false &&
                    option.amountToFree != null &&
                    option.amountToFree > 0
                "
                class="text-xs text-blue-600 font-semibold mt-1"
              >
                {{
                  $t("shipping_calculator.ld_amount_to_free", {
                    amount: `${currency}${option.amountToFree.toFixed(2)}`
                  })
                }}
              </div>
              <div
                class="text-sm text-gray-600"
                v-if="option.estimatedShipping"
              >
                Estimated dispatch: {{ option.estimatedShipping }}
              </div>
              <div
                v-if="option.carrierDeliveryDays || option.deliveryDays"
                class="text-sm text-gray-700"
              >
                <span v-if="option.carrierDeliveryDays">
                  Arrives: {{ option.carrierDeliveryDays }}
                </span>
                <span v-else-if="option.deliveryDays">
                  Arrives in {{ option.deliveryDays }}
                  {{ option.deliveryDays > 1 ? "days" : "day" }}
                </span>
              </div>
              <div
                v-if="option.estimatedDeliveryDate"
                class="text-xs text-gray-500"
              >
                Estimated delivery:
                {{ formatDeliveryDate(option.estimatedDeliveryDate) }}
              </div>
              <div class="flex gap-1 mt-1" v-if="hasShippingFlags(option)">
                <span
                  v-if="option.guaranteedService"
                  class="bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-xs"
                  >Guaranteed</span
                >
                <span
                  v-if="option.trackable"
                  class="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs"
                  >Trackable</span
                >
              </div>
              <div class="text-xs text-gray-500" v-if="option.warehouse_name">
                {{ option.warehouse_name }} ({{ option.warehouse_code }})
              </div>
            </div>
            <div class="flex flex-col items-end">
              <div class="text-blue-800 font-bold text-lg">
                {{ currency }}{{ option.price.toFixed(2) }}
              </div>
              <div class="text-xs text-gray-500" v-if="option.baseCost">
                {{ $t("shipping_calculator.base") }} {{ currency
                }}{{ option.baseCost.toFixed(2) }}
              </div>
            </div>
          </div>
        </div>
      </template>
      <div v-else class="w-full flex justify-center items-center py-8">
        <BaseSmallSpinner />
      </div>
    </div>
  </BaseModal>
</template>
<script>
import { calculateShipping } from "@/api/webApi.js";
import ResidentialBadge from "@/components/shared/ResidentialBadge.vue";
const states = require("@/constants/states.json");

export default {
  components: {
    ResidentialBadge
  },
  props: {
    value: {
      type: Boolean,
      required: true
    },
    products: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    postalCode: {
      type: String,
      required: false,
      default() {
        return "";
      }
    },
    country: {
      type: String,
      required: false,
      default() {
        return "US";
      }
    },
    state: {
      type: String,
      required: false,
      default() {
        return "FL";
      }
    },
    // Calle de la orden: con ella ShipStation dice si es residencial
    street1: {
      type: String,
      required: false,
      default: ""
    },
    street2: {
      type: String,
      required: false,
      default: ""
    },
    city: {
      type: String,
      required: false,
      default: ""
    }
  },
  data() {
    return {
      intPostalCode: this.postalCode,
      // Lo que devolvió el API: { residential, verified, status }
      residentialCheck: null,
      intCountry: this.country,
      intState: this.state,
      options: [],
      packageData: null,
      shippingPackage: null,
      loading: false,
      showInfoForm: window.innerWidth >= 768, // Collapsed on mobile, expanded on desktop
      calculationId: null,
      customBox: {
        height: 0,
        width: 0,
        length: 0,
        weight: 0
      },
      useCustomPackage: false,
      useBoxConstraints: false,
      boxConstraints: {
        maxWeight: null,
        maxLength: null,
        maxWidth: null,
        maxHeight: null,
        maxLengthPlusGirth: null
      },
      warnings: [],
      expandedBoxes: {},
      selectedCarrier: "all",
      carrierLogos: {
        UPS: require("@/assets/icons/carriers/ups.svg"),
        Fedex: require("@/assets/icons/carriers/fedex.svg"),
        USPS: require("@/assets/icons/carriers/usps.svg")
      }
    };
  },
  computed: {
    getAdminSettings() {
      return this.$store.getters["general/getAdminSettings"];
    },
    currency() {
      const currencyObj = this.getAdminSettings.get("currency");
      return currencyObj?.value || "$";
    },
    residentialNote() {
      const check = this.residentialCheck;
      if (check.verified) {
        return this.$t("shipping_calculator.residential_hint");
      }
      return this.$t(
        check.status === "no_street"
          ? "shipping_calculator.residential_no_street_hint"
          : "shipping_calculator.residential_unverified_hint"
      );
    },
    statesFixed() {
      const keys = Object.keys(states);
      return keys.map(key => {
        return {
          shortCode: key,
          label: states[key]
        };
      });
    },
    totalItems() {
      return this.products.reduce((sum, product) => {
        return sum + (parseInt(product.quantity) || 0);
      }, 0);
    },
    productsInfo() {
      return `${this.products.length} products (${this.totalItems} items)`;
    },
    availableCarriers() {
      const carriers = {};
      this.options.forEach(option => {
        const company = option.company;
        if (!carriers[company]) {
          carriers[company] = {
            name: company,
            count: 0,
            logo: this.carrierLogos[company] || null
          };
        }
        carriers[company].count++;
      });
      return Object.values(carriers);
    },
    filteredOptions() {
      let filtered;
      if (this.selectedCarrier === "all") {
        filtered = this.options;
      } else {
        filtered = this.options.filter(
          option => option.company === this.selectedCarrier
        );
      }
      // Ineligible options last, then by price from lowest to highest
      return filtered.slice().sort((a, b) => {
        const aIneligible = a.eligible === false ? 1 : 0;
        const bIneligible = b.eligible === false ? 1 : 0;
        if (aIneligible !== bIneligible) return aIneligible - bIneligible;
        return a.price - b.price;
      });
    },
    activeDepartmentId() {
      return this.$store.getters["general/getActiveDepartment"]?.full
        .departamentoId;
    }
  },
  watch: {
    value(val) {
      if (val) {
        // La dirección de la orden pudo cambiar desde la última vez
        this.intPostalCode = this.postalCode;
        this.intCountry = this.country;
        this.intState = this.state;
        this.residentialCheck = null;
        if (this.products.length == 0) {
          this.useCustomPackage = true;
        }
        if (this.country == false) {
          this.intCountry = "US";
        }
        // Automatically calculate shipping when modal opens
        this.$nextTick(() => {
          this.calculate();
        });
      }
    }
  },
  methods: {
    formatDeliveryDate(iso) {
      if (!iso) return "";
      // Strip trailing Z so the timestamp is treated as local time (carrier-quoted local arrival),
      // not converted to/from UTC which would shift the hours.
      const local = String(iso).replace(/Z$/, "");
      const d = new Date(local);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
      });
    },
    hasShippingFlags(option) {
      return !!(option.guaranteedService || option.trackable);
    },
    localDeliveryReason(type) {
      const reasons = {
        below_min_order: this.$t("shipping_calculator.ld_below_min_order"),
        out_of_range: this.$t("shipping_calculator.ld_out_of_range"),
        disabled: this.$t("shipping_calculator.ld_disabled"),
        no_rate: this.$t("shipping_calculator.ld_no_rate")
      };
      return reasons[type] || type;
    },
    localDeliveryLabel(type) {
      const labels = {
        free: this.$t("shipping_calculator.ld_free"),
        local_zip: this.$t("shipping_calculator.ld_block_rate"),
        local_open_rate: this.$t("shipping_calculator.ld_open_rate")
      };
      return labels[type] || type;
    },
    calculate(forceRecalculate = false) {
      this.warnings = [];
      this.loading = true;
      this.expandedBoxes = {};

      // Prepare box constraints only if enabled and has values
      let constraints = null;
      if (this.useBoxConstraints) {
        constraints = {};
        if (this.boxConstraints.maxWeight)
          constraints.maxWeight = this.boxConstraints.maxWeight;
        if (this.boxConstraints.maxLength)
          constraints.maxLength = this.boxConstraints.maxLength;
        if (this.boxConstraints.maxWidth)
          constraints.maxWidth = this.boxConstraints.maxWidth;
        if (this.boxConstraints.maxHeight)
          constraints.maxHeight = this.boxConstraints.maxHeight;
        if (this.boxConstraints.maxLengthPlusGirth)
          constraints.maxLengthPlusGirth = this.boxConstraints.maxLengthPlusGirth;

        // Only send if at least one constraint is set
        if (Object.keys(constraints).length === 0) {
          constraints = null;
        }
      }

      calculateShipping(
        this.products,
        this.intPostalCode,
        // La calle sólo vale para el código postal de la orden: si el vendedor lo cambia,
        // se cotiza sin calle (residencial)
        this.intPostalCode === this.postalCode
          ? { street1: this.street1, street2: this.street2, city: this.city }
          : {},
        this.intCountry,
        this.intState,
        this.useCustomPackage ? this.customBox : false,
        constraints,
        this.activeDepartmentId,
        forceRecalculate
      )
        .then(res => {
          // Check if response is empty (no package data or shipping options)
          if (!res.packageToCalculate && !res.shippingOptions) {
            // Handle empty order response
            this.packageData = null;
            this.shippingPackage = null;
            this.options = [];
            this.warnings = [];
            this.calculationId = null;
            return;
          }

          this.packageData = res.packageToCalculate;
          this.shippingPackage = res.packageToCalculate; // Keep for backwards compatibility

          const localDeliveryOptions = (
            res.localDeliveryOptions ||
            res.local_delivery_options ||
            []
          ).map(o => {
            const depName = o.depositoName || o.deposito_name || o.depositoId;
            const depId = o.depositoId || o.deposito_id;
            const distance = o.distanceMiles || o.distance_miles;
            const type = o.type;
            const eligible = o.eligible !== false;
            const amountToFree = o.amountToFree ?? o.amount_to_free ?? null;
            return {
              code: "local_delivery",
              serviceCode: `local_delivery_${depId}`,
              service: `Local delivery from ${depName} (${distance} mi)`,
              company: "Local Delivery",
              price: Number(o.price) || 0,
              baseCost: Number(o.price) || 0,
              warehouse_id: depId,
              warehouse_name: depName,
              localDeliveryType: type,
              eligible,
              amountToFree
            };
          });

          this.options = [
            ...(res.shippingOptions || []),
            ...localDeliveryOptions
          ];
          this.warnings = res.warnings;
          this.calculationId = res.calculation_id || res.calculationId || null;
          this.residentialCheck = res.residential || null;

          // Initialize all boxes as collapsed
          if (this.packageData && this.packageData.boxes) {
            this.packageData.boxes.forEach((box, index) => {
              this.$set(this.expandedBoxes, index, false);
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    toggleBox(index) {
      this.$set(this.expandedBoxes, index, !this.expandedBoxes[index]);
    },
    selectShippingOption(option) {
      this.$emit("shippingSelected", {
        option: option,
        calculationId: this.calculationId
      });
    }
  }
};
</script>
<style lang="postcss">
#shipping-calculator-modal .modal-content {
  max-width: 950px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

#shipping-calculator-modal .modal-body {
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
}

@media (max-width: 768px) {
  #shipping-calculator-modal .modal-content {
    max-height: 95vh;
  }
}

#shipping-calculator-modal .residential-note {
  @apply bg-yellow-100 text-yellow-900 text-sm;
}
</style>
